import React from 'react';
import { Target, TrendingUp, Star } from 'lucide-react';
import type { Month, Todo } from '../types';

interface MonthSummaryProps {
  month: Month;
  activeTasks: Todo[];
  draftTasks: Todo[];
  onStartMonthlyReview: () => void;
}

const MonthSummary: React.FC<MonthSummaryProps> = ({
  month,
  activeTasks,
  draftTasks,
  onStartMonthlyReview
}) => {
  const formatMonthRange = (month: Month) => {
    const date = new Date(month.start_date);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getTaskStats = () => {
    const allTasks = [...activeTasks, ...draftTasks];
    const uniqueTasks = Array.from(new Map(allTasks.map(task => [task.id, task])).values());
    
    const completed = uniqueTasks.filter(task => task.status === 'completed').length;
    const inProgress = uniqueTasks.filter(task => task.status === 'in_progress').length;
    const total = uniqueTasks.length;

    return { completed, inProgress, total };
  };

  const stats = getTaskStats();
  const completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  const getMonthlyReviewStatus = () => {
    if (!month) return null;
    
    const now = new Date();
    const monthEnd = new Date(month.end_date);
    const isMonthOver = now > monthEnd;
    const isOverdue = isMonthOver && !month.is_review_completed;
    
    return {
      isCompleted: month.is_review_completed,
      isOverdue,
      shouldGlow: isOverdue || (!month.is_review_completed && isMonthOver)
    };
  };

  const reviewStatus = getMonthlyReviewStatus();

  return (
    <div className="w-full bg-white dark:bg-gray-800 p-4 lg:p-6 space-y-6">

      {/* Task Statistics */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            {formatMonthRange(month)}
          </h3>
          <button
            onClick={onStartMonthlyReview}
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              reviewStatus?.shouldGlow 
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white animate-pulse' 
                : reviewStatus?.isCompleted
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {reviewStatus?.isCompleted ? 'Review Complete' : 'Monthly Review'}
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between items-center text-xs text-gray-600 mb-1">
            <span>{stats.completed} of {stats.total} tasks</span>
            <span>{completionRate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stats.total}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-blue-600">{stats.inProgress}</div>
            <div className="text-xs text-gray-500">In Progress</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-green-600">{stats.completed}</div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
        </div>
      </div>

      {/* Monthly Goals */}
      {month.goals && month.goals.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
            <Target className="w-4 h-4 mr-2 text-blue-600" />
            Monthly Goals
          </h3>
          <ul className="space-y-2">
            {month.goals.map((goal, index) => (
              <li key={index} className="flex items-start text-sm">
                <Star className="w-3 h-3 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300">{goal}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MonthSummary;